import type { HotkeyOverrides } from '@/lib/hotkeys/applyHotkeyOverride';

/**
 * Collapse shortcut-preference writes onto one in-flight request. Each upsert
 * replaces the whole `keyboardShortcuts` value, so a slower earlier snapshot
 * must not land after a newer one.
 */
export interface ShortcutWriteQueue {
  isIdle: () => boolean;
  enqueue: (overrides: HotkeyOverrides) => void;
}

export function createShortcutWriteQueue(
  send: (overrides: HotkeyOverrides) => Promise<void>,
  onError?: (error: unknown) => void,
) {
  let pending: HotkeyOverrides | null = null;
  let flushing = false;

  async function flush() {
    if (flushing) return;
    flushing = true;
    try {
      while (pending !== null) {
        const snapshot = pending;
        pending = null;
        try {
          await send(snapshot);
        } catch (error) {
          // A newer edit is already queued; let that write replace this one.
          if (pending !== null) continue;
          onError?.(error);
          break;
        }
      }
    } finally {
      flushing = false;
      if (pending !== null) {
        void flush();
      }
    }
  }

  return {
    isIdle() {
      return !flushing && pending === null;
    },
    enqueue(overrides: HotkeyOverrides) {
      pending = overrides;
      // Defer so several edits in the same turn collapse to the latest
      // snapshot before a request starts. An in-flight flush still picks up
      // anything queued while it is awaiting.
      if (!flushing) {
        queueMicrotask(() => {
          void flush();
        });
      }
    },
  };
}
